/**
 * F17.2.6 — Pagina builder Controsoffitto.
 *
 * Routing condizionale (specchio di ConfiguratorV2.tsx):
 *  - `?id=<uuid>` → modifica controsoffitto esistente (builder)
 *  - `?new=true`  → crea da zero (builder vuoto)
 *  - nessun param → redirect a /ceilings (lista tipologie esistente)
 *
 * Layout: riusa `ConfiguratorV2Layout` (split 40/60 desktop · tabs mobile) e
 * `ConfiguratorHeader` (nome editabile + salva + kebab). I pannelli composition
 * e visualization sono **placeholder MVP** in attesa di F17.2.7 (CeilingLayersPanel)
 * e F17.2.8 (CeilingVisualizationPanel con sezione SVG orizzontale + pianta riflessa).
 *
 * Hook: `useCeilingConfiguratorState` (F17.2.3) per stato + save.
 * Costi: `computeCeilingCosts` (F17.2.5) per il preview €/m².
 * Modello sezione: `buildCeilingSectionModel` (F17.2.4) per il preview testuale dei blocchi.
 */
import React from 'react';
import { useSearchParams, Navigate, useNavigate } from 'react-router-dom';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import ConfiguratorV2Layout from '@/components/configurator-v2/ConfiguratorV2Layout';
import ConfiguratorHeader from '@/components/configurator-v2/header/ConfiguratorHeader';
import { useCeilingConfiguratorState } from '@/components/configurator-v2/hooks/useCeilingConfiguratorState';
import {
  buildCeilingSectionModel,
  generateCeilingTitle,
} from '@/components/configurator-v2/visualization/section-view/ceilingModel';
import { computeCeilingCosts } from '@/components/configurator-v2/hooks/computeCeilingCosts';
import CeilingLayersPanel from '@/components/configurator-v2/composition/CeilingLayersPanel';
import CeilingVisualizationShell from '@/components/configurator-v2/visualization/CeilingVisualizationShell';
import AddCeilingToEstimateDialog from '@/components/configurator-v2/estimates/AddCeilingToEstimateDialog';
import DeleteStratigraphyDialog from '@/components/configurator-v2/list-view/DeleteStratigraphyDialog';
import { useDuplicateStratigraphy } from '@/components/configurator-v2/hooks/useDuplicateStratigraphy';
import { useCeilingSystemCode } from '@/components/configurator-v2/hooks/useCeilingSystemCode';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCeilingTypologies } from '@/hooks/useCeilingTypologies';
import EstimateContextBanner from '@/components/configurator-v2/estimates/EstimateContextBanner';
import { useSyncEstimateRowAfterSave } from '@/hooks/useSyncEstimateRowAfterSave';

const CeilingConfigurator: React.FC = () => {
  const [searchParams] = useSearchParams();
  const stratigraphyId = searchParams.get('id');
  const isNew = searchParams.get('new') === 'true';
  const typologyCode = searchParams.get('typology');
  // Contesto preventivo. `estimate` = si sta lavorando dentro un preventivo;
  // `fromEstimateRow` = si sta modificando la parete di UNA riga esistente.
  // Il configuratore dei controsoffitti non li leggeva affatto: la copia veniva
  // creata e la riga agganciata, ma al salvataggio il prezzo della riga NON si
  // riallineava e non si tornava al preventivo — cioe' il problema che la
  // correzione del 27 agosto aveva risolto solo per le pareti.
  const estimateId = searchParams.get('estimate');
  const fromEstimateRow = searchParams.get('fromEstimateRow');

  // Senza param → torna alla lista tipologie (skeleton già esistente in /ceilings)
  if (!stratigraphyId && !isNew) {
    return <Navigate to="/ceilings" replace />;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-zinc-50/50">
        <AppSidebar />
        <SidebarInset className="flex flex-col">
          <Header />
          <main className="flex-1">
            {estimateId && (
              <EstimateContextBanner
                estimateId={estimateId}
                mode={fromEstimateRow ? 'modifica' : 'aggiunta'}
              />
            )}
            <CeilingBuilderView
              stratigraphyId={stratigraphyId}
              typologyCode={isNew ? typologyCode : null}
              preselectedEstimateId={estimateId}
              fromEstimateRowId={fromEstimateRow}
            />
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

const CeilingBuilderView: React.FC<{
  stratigraphyId: string | null;
  typologyCode: string | null;
  preselectedEstimateId?: string | null;
  fromEstimateRowId?: string | null;
}> = ({ stratigraphyId, typologyCode, preselectedEstimateId, fromEstimateRowId }) => {
  const navigate = useNavigate();
  const state = useCeilingConfiguratorState({ stratigraphyId });
  // Stessa logica delle pareti, stesso hook: la regola "mai riscrivere un
  // preventivo gia' uscito" vive in un posto solo.
  const syncEstimateRow = useSyncEstimateRowAfterSave(
    fromEstimateRowId ? preselectedEstimateId ?? undefined : undefined,
  );
  const { data: typologies = [] } = useCeilingTypologies();

  // Quando l'utente arriva da /ceilings con ?new=true&typology=<code>, applica
  // i defaults della tipologia (one-shot, solo al primo render dopo load).
  const typologyAppliedRef = React.useRef(false);
  React.useEffect(() => {
    if (typologyAppliedRef.current) return;
    if (!typologyCode || stratigraphyId) return;
    if (typologies.length === 0) return;
    const typology = typologies.find((t) => t.code === typologyCode);
    if (!typology) return;
    state.applyTypology({
      structure_type: typology.structure_type,
      attachment_type: typology.attachment_type,
      hanger_spacing_mm: typology.hanger_spacing_mm,
      primary_profile_spacing_mm: typology.primary_profile_spacing_mm,
      secondary_profile_spacing_mm: typology.secondary_profile_spacing_mm,
    });
    typologyAppliedRef.current = true;
  }, [typologyCode, stratigraphyId, typologies, state]);

  // Cost per hour corrente (settings org-wide). Stesso pattern del wall configurator.
  const { data: costPerHour = 30 } = useQuery({
    queryKey: ['cost_per_hour'],
    queryFn: async () => {
      const { data } = await supabase
        .from('configurator_settings')
        .select('value')
        .eq('key', 'cost_per_hour')
        .maybeSingle();
      return Number(data?.value ?? 30) || 30;
    },
    staleTime: 60_000,
  });

  // Modello sezione (deriva da layers + ceiling state). Computato in real-time.
  const model = React.useMemo(
    () => buildCeilingSectionModel(state.layers, state.ceiling),
    [state.layers, state.ceiling],
  );

  // Breakdown costi €/m². NON include perimetro (calcolato in AddCeilingToEstimateDialog).
  // Pendino: per ora null (sarà passato dal CeilingComposeShell quando l'utente lo sceglie).
  const breakdown = React.useMemo(
    () => computeCeilingCosts(state.layers, state.ceiling, costPerHour),
    [state.layers, state.ceiling, costPerHour],
  );

  const ceilingTitle = state.layers.length > 0 ? generateCeilingTitle(model) : null;
  // System code derivato live da state+layers (CS-SUSP-DOUBLE, CS-FIRE, ecc.)
  const systemCode = useCeilingSystemCode({ layers: state.layers, ceiling: state.ceiling });

  // F17.2.13: dialog "Aggiungi a preventivo"
  const [showAddDialog, setShowAddDialog] = React.useState(false);
  // Polish: dialog "Elimina" riusabile (al posto di window.confirm)
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  // Tracciamo l'id post-save per assicurarci che il dialog parta da uno stratigraphyId valido.
  const [savedStratigraphyId, setSavedStratigraphyId] = React.useState<string | null>(state.id ?? null);
  React.useEffect(() => {
    if (state.id) setSavedStratigraphyId(state.id);
  }, [state.id]);

  // Polish: duplicate stratigrafia dal builder (mirror wall pattern)
  const { duplicateStratigraphy, isDuplicating } = useDuplicateStratigraphy();
  const handleDuplicateFromBuilder = async () => {
    let sourceId = state.id;
    if (state.isDirty && state.canSave) {
      sourceId = await state.saveAsync();
    }
    if (!sourceId) return;
    try {
      const { id: newId } = await duplicateStratigraphy(sourceId);
      if (newId) navigate(`/ceilings/configurator?id=${newId}`);
    } catch {
      // toast d'errore gestito dal hook
    }
  };

  const handleSaveClick = async () => {
    // Per ora salva senza name-conflict dialog. Il pattern wall ha un dialog dedicato
    // (NameConflictDialog) per gestire duplicati — replicabile in F17.2.X se serve.
    if (fromEstimateRowId) {
      const savedId = await state.saveAsync();
      if (savedId) await syncEstimateRow(savedId, fromEstimateRowId);
      return;
    }
    state.save();
  };

  // Pattern wall (vedi ConfiguratorV2.doAddToEstimate): SALVA prima, poi apre il dialog.
  // Senza questo, se l'utente clicca "Aggiungi a preventivo" su una stratigrafia non
  // ancora salvata, il dialog si apre con stratigraphyId=null e il submit fallisce.
  const handleAddToEstimateClick = async () => {
    if (!state.canSave && !state.id) return;
    const newId = await state.saveAsync();
    if (newId) {
      setSavedStratigraphyId(newId);
      setShowAddDialog(true);
    }
  };

  // canAddToEstimate: allineato al wall configurator — bottone visibile appena
  // c'è qualcosa di salvabile o già salvato (UX coerente).
  const canAddToEstimate = state.canSave || !!state.id;

  if (state.isLoading) {
    return (
      <div className="container mx-auto max-w-screen-2xl px-6 py-12">
        <div className="text-center text-muted-foreground">Caricamento controsoffitto…</div>
      </div>
    );
  }
  if (state.loadError) {
    return (
      <div className="container mx-auto max-w-screen-2xl px-6 py-12">
        <div className="text-center text-destructive">Errore: {state.loadError}</div>
      </div>
    );
  }

  return (
    <ConfiguratorV2Layout
      header={
        <ConfiguratorHeader
          name={state.name}
          onNameChange={state.setName}
          systemCode={systemCode}
          typology={ceilingTitle}
          supplierFilterLabel={null}
          isCertified={state.isCertified}
          isDirty={state.isDirty}
          isSaving={state.isSaving}
          onSave={handleSaveClick}
          canSave={state.canSave}
          canAddToEstimate={canAddToEstimate}
          onAddToEstimate={handleAddToEstimateClick}
          canDelete={!!state.id}
          onDelete={() => setShowDeleteDialog(true)}
          canDuplicate={!!state.id || state.canSave}
          onDuplicate={handleDuplicateFromBuilder}
          isDuplicating={isDuplicating}
          editingForEstimate={!!fromEstimateRowId}
        />
      }
      composition={
        <CeilingLayersPanel
          layers={state.layers}
          ceiling={state.ceiling}
          onAddLayer={state.addLayer}
          onUpdateLayer={state.updateLayer}
          onRemoveLayer={state.removeLayer}
          onDuplicateLayer={state.duplicateLayer}
          onReorderLayers={state.reorderLayers}
          onSetMaterial={state.setMaterial}
          onSetScrewMaterial={state.setScrewMaterial}
          onSetScrewQuantity={state.setScrewQuantity}
          onReapplyScrewSuggestion={state.reapplyScrewSuggestion}
          onSetCeiling={state.setCeiling}
          isCertified={state.isCertified}
          certification={state.certification}
          onSetCertification={state.setCertification}
        />
      }
      visualization={
        <>
          <CeilingVisualizationShell
            layers={state.layers}
            ceiling={state.ceiling}
            model={model}
            breakdown={breakdown}
            ceilingTitle={ceilingTitle}
            stratigraphyName={state.name}
            stratigraphyDescription={state.description}
          />
          <AddCeilingToEstimateDialog
            open={showAddDialog}
            onOpenChange={setShowAddDialog}
            stratigraphyId={savedStratigraphyId}
            stratigraphyName={state.name}
            stratigraphyDescription={state.description}
            costPerSqm={breakdown.totalCost}
            // Il dialogo lo accettava gia`, ma la pagina non leggeva `?estimate=`
            // e quindi non glielo passava mai: arrivando da un preventivo
            // bisognava riselezionarlo a mano dal menu. Le pareti lo fanno da
            // sempre.
            preselectedEstimateId={preselectedEstimateId}
          />
          <DeleteStratigraphyDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            stratigraphyId={state.id}
            stratigraphyName={state.name}
            onDeleted={() => navigate('/ceilings')}
          />
        </>
      }
    />
  );
};

export default CeilingConfigurator;
